import React, { useEffect, useState, useMemo } from 'react';
import FileDownload from 'js-file-download';
import { Button, Divider, Spinner, Typography } from '@telus-uds/components-web';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import ValidateLoader from '../UI/validateLoader';
import { CONSTANT } from '../utils/constant';
import request, { isAdmin } from '../utils/request';
import Filters from './Filters';
import { DEFECTS_PER_PAGE, FILTERS_INIT_STATE } from './constants';
import { useLocaleContext } from '../context/LocaleContext';
import EN from '../../EN.json';
import FR from '../../FR.json';
import { TableBottomBar } from './css';
import Pagination from './Pagination';
import Table from './Table';
import {
  useFiltersActionsContext,
  useFiltersContext
} from '../context/FiltersContext';

const DefectManagement = () => {
  const locale = useLocaleContext();
  const historyRef = useHistory();

  const { search } = window.location;
  let { defectId } = queryString.parse(search) || null;
  const filterLastStatus = useFiltersContext();
  const {
    setFilters: setFilterLastStatus,
    areFiltersTouched
  } = useFiltersActionsContext();

  const {
    DEFECT_MANAGEMENT: {
      Export_Defects,
      DEFECT_TABLE = {},
      DEFECTS = '',
      NO_DEFECTS = ''
    } = {}
  } = locale === 'en-US' ? EN : FR;

  const {
    KEYS: {
      ROLES: { ADMIN }
    }
  } = CONSTANT;

  const headerList = DEFECT_TABLE;
  if (!isAdmin) {
    delete headerList.PRIORITY;
  }
  const { getProfile } = request;
  const profile = getProfile();
  const { role, email: userEmail } = profile;
  // States
  const [defects, setDefects] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [totalDefects, setTotalDefects] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [fetchData, setFetchData] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [isExportingDefects, setExportingDefects] = useState(false);

  const currentUserAssigneeEmail = useMemo(() => {
    if (role.name === ADMIN && assignedUsers.length) {
      const user = assignedUsers.find(({ email }) => email === userEmail);
      if (user) {
        const { email: foundEmail } = user;
        return foundEmail;
      }
      return null;
    }
    return null;
  }, [assignedUsers]);

  // API Call to Fetch all Defects
  function fetchAllDefects(query = {}) {
    setPageLoading(true);
    if (role.name === ADMIN) {
      setPageLoading(true);
      request
        .get('/defects', { params: { ...query, page: pageNumber, perPage: DEFECTS_PER_PAGE } })
        .then(res => {
          const {
            data: { totalCount = 0, status = 1, message = '', innerData = [] }
          } = res;
          if (status === 1 && message) {
            setDefects(innerData);
            setTotalDefects(totalCount);
            setFetchData(false);
            setPageLoading(false);
          }
        });
      request.get('/defects/admins').then(res => {
        const {
          data: { adminList = [] }
        } = res;
        setAssignedUsers(adminList);
      });
    } else {
      request.get('/defects/mine', { params: query }).then(res => {
        const {
          data: { totalCount = 0, status = 1, message = '', innerData = [] }
        } = res;
        if (status === 1 && message) {
          setDefects(innerData);
          setTotalDefects(totalCount);
          setFetchData(false);
          setPageLoading(false);
        }
      });
    }
  }

  const applyFilter = filter => {
    setFilterLastStatus(filter, {
      shouldUpdateTouchedStatus: !!currentUserAssigneeEmail
    });
    Object.keys(FILTERS_INIT_STATE).forEach(key =>
      // eslint-disable-next-line no-param-reassign
      filter[key] === '' ? delete filter[key] : filter[key]
    );
    fetchAllDefects(filter);
  };

  const reset = () => {
    setFilterLastStatus({});
    fetchAllDefects();
  };

  const onExportClick = () => {
    setExportingDefects(true);
    const filter = filterLastStatus;
    Object.keys(FILTERS_INIT_STATE).forEach(key =>
      // eslint-disable-next-line no-param-reassign
      filter[key] === '' ? delete filter[key] : filter[key]
    );
    request
      .post(
        '/defects/export',
        { language: locale, role: role.name },
        { responseType: 'arraybuffer', params: filter }
      )
      .then(response => response.data)
      .then(fileData => {
        FileDownload(fileData, 'defects.csv');
        setExportingDefects(false);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (pageNumber || fetchData) {
      if (filterLastStatus || currentUserAssigneeEmail) {
        applyFilter({
          ...filterLastStatus,
          ...(currentUserAssigneeEmail && !areFiltersTouched
            ? { assignee: currentUserAssigneeEmail }
            : {})
        });
      } else fetchAllDefects();
    }
  }, [pageNumber, fetchData, assignedUsers.length]);

  return (
    <>
      <Filters
        onApplyFilters={applyFilter}
        onResetFilters={reset}
        role={role}
        assignedUsers={assignedUsers}
        defaultFilters={{
          ...filterLastStatus,
          ...(currentUserAssigneeEmail && !areFiltersTouched
            ? { assignee: currentUserAssigneeEmail }
            : {})
        }}
      />
      <Divider space={3} variant={{ inverse: true }} />
      {pageLoading && <ValidateLoader message="Loading" />}
      {!pageLoading && !totalDefects && (
        <Typography heading="h3" variant={{ size: 'h3' }} block> {NO_DEFECTS}</Typography>
      )}
      {!pageLoading && totalDefects > 0 && (
        <>
          <Table
            tableCaption={`${DEFECTS} (${totalDefects})`}
            setFetchData={setFetchData}
            header={headerList}
            data={defects}
            userProfile={profile}
            defectId={defectId}
            onDefectSelected={val => {
              if (val && val.id) {
                historyRef.push({
                  search: `?defectId=${val.id}`
                });
              } else {
                historyRef.push({
                  search: ''
                });
                defectId = null;
                if (filterLastStatus) {
                  applyFilter(filterLastStatus);
                }
              }
            }}
          />
          <Pagination
            pageNumber={pageNumber}
            totalDefects={totalDefects}
            defectsPerPage={DEFECTS_PER_PAGE}
            setPageNumber={setPageNumber}
          />
          {role && role.name === ADMIN ? (
            <TableBottomBar>
              <Spinner
                show={isExportingDefects}
                variant={{ size: 'small' }}
                label="Request is processing."
                inline
              >
                <Button onPress={onExportClick}>{Export_Defects}</Button>
              </Spinner>
            </TableBottomBar>
          ) : (
            ''
          )}
        </>
      )}
    </>
  );
};

export default DefectManagement;
